import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Spinner,
    Input,
    Textarea,
    Autocomplete,
    AutocompleteItem,
} from "@heroui/react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { usePostData, useFetchData } from "@/hooks";
import { showToast } from "@/utils";
import { ErrorState, ThemedText, PageHeader } from "@/components/ui";
import { LuArrowLeft, LuSave } from "react-icons/lu";
import { useEffect } from "react";

const MasterCashCreate = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;

    // Form Hooks
    const {
        register,
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            name: "",
            master_cash_category_id: "",
            journal_account_id: "",
            description: "",
        },
    });

    // Fetch data if editing
    const fetchUrl = isEdit ? `master-cash/${id}/edit` : null;
    const {
        data: initialData,
        isLoading: isFetching,
        error: fetchError,
        refetch,
    } = useFetchData(fetchUrl);

    // Fetch Select Options
    const { data: categories, isLoading: isLoadingCategories } = useFetchData(
        "select/master-cash-category",
    );
    const { data: accounts, isLoading: isLoadingAccounts } = useFetchData(
        "select/journal-account",
    );

    // Reset form when initialData loads
    useEffect(() => {
        if (initialData?.data) {
            const data = initialData.data;
            reset({
                name: data.name || "",
                master_cash_category_id: data.master_cash_category_id
                    ? String(data.master_cash_category_id)
                    : "",
                journal_account_id: data.journal_account_id
                    ? String(data.journal_account_id)
                    : "",
                description: data.description || "",
            });
        }
    }, [initialData, reset]);

    // Mutation
    const endpoint = isEdit ? `master-cash/${id}/update` : "master-cash/store";
    const mutation = usePostData(endpoint);

    const onSubmit = (data) => {
        mutation.mutate(data, {
            method: isEdit ? "put" : "post",
            onSuccess: () => {
                showToast(
                    "success",
                    "Berhasil",
                    `Master Cash berhasil ${isEdit ? "diperbarui" : "ditambahkan"}.`,
                );
                navigate("/master-cash/data");
            },
            onError: (err) => {
                showToast(
                    "error",
                    "Gagal",
                    err.response?.data?.message || "Terjadi kesalahan.",
                );
            },
        });
    };

    if (isEdit && fetchError) {
        return (
            <ErrorState
                title="Gagal Memuat Data"
                description="Terjadi kesalahan saat memuat data."
                onAction={refetch}
            />
        );
    }

    return (
        <div className="mx-auto flex max-w-3xl flex-col gap-8">
            <div className="flex gap-4">
                <Button isIconOnly variant="light" onPress={() => navigate(-1)}>
                    <LuArrowLeft size={20} />
                </Button>
                <PageHeader
                    title={isEdit ? "Edit Master Cash" : "Tambah Master Cash"}
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Keuangan" },
                        { label: "Master Cash", href: "/master-cash/data" },
                        { label: isEdit ? "Edit" : "Tambah" },
                    ]}
                />
            </div>

            <Card className="border-default-200 w-full border" shadow="none">
                <CardHeader className="bg-default-100/70 px-6 py-4">
                    <ThemedText className="font-semibold">
                        Informasi Master Cash
                    </ThemedText>
                </CardHeader>
                <CardBody className="p-6">
                    {isEdit && isFetching ? (
                        <div className="flex h-48 w-full items-center justify-center">
                            <Spinner label="Memuat data..." color="primary" />
                        </div>
                    ) : (
                        <form
                            onSubmit={handleSubmit(onSubmit)}
                            className="flex flex-col gap-4"
                            noValidate
                        >
                            <Input
                                {...register("name", {
                                    required: "Nama Master Cash wajib diisi",
                                })}
                                label="Nama Master Cash"
                                placeholder="Contoh: Kas Besar, Bank BCA"
                                variant="bordered"
                                errorMessage={errors.name?.message}
                                isInvalid={!!errors.name}
                                isRequired
                            />

                            <Controller
                                name="master_cash_category_id"
                                control={control}
                                rules={{ required: "Kategori wajib dipilih" }}
                                render={({ field }) => (
                                    <Autocomplete
                                        label="Kategori"
                                        placeholder="Pilih Kategori"
                                        variant="bordered"
                                        isLoading={isLoadingCategories}
                                        defaultItems={categories || []}
                                        selectedKey={
                                            field.value
                                                ? String(field.value)
                                                : null
                                        }
                                        onSelectionChange={(key) =>
                                            field.onChange(key)
                                        }
                                        errorMessage={
                                            errors.master_cash_category_id
                                                ?.message
                                        }
                                        isInvalid={
                                            !!errors.master_cash_category_id
                                        }
                                        isRequired
                                    >
                                        {(item) => (
                                            <AutocompleteItem
                                                key={String(item.value)}
                                                textValue={item.title}
                                            >
                                                {item.title}
                                            </AutocompleteItem>
                                        )}
                                    </Autocomplete>
                                )}
                            />

                            <Controller
                                name="journal_account_id"
                                control={control}
                                render={({ field }) => (
                                    <Autocomplete
                                        label="Akun Jurnal "
                                        placeholder="Pilih Akun Jurnal"
                                        variant="bordered"
                                        isLoading={isLoadingAccounts}
                                        defaultItems={accounts || []}
                                        selectedKey={
                                            field.value
                                                ? String(field.value)
                                                : null
                                        }
                                        onSelectionChange={(key) =>
                                            field.onChange(key)
                                        }
                                    >
                                        {(item) => (
                                            <AutocompleteItem
                                                key={String(item.value)}
                                                textValue={item.title}
                                            >
                                                {item.title}
                                            </AutocompleteItem>
                                        )}
                                    </Autocomplete>
                                )}
                            />

                            <Textarea
                                {...register("description")}
                                label="Keterangan"
                                placeholder="Tambahkan keterangan (opsional)"
                                variant="bordered"
                            />

                            <div className="mt-4 flex justify-end gap-2">
                                <Button
                                    onPress={() =>
                                        navigate("/master-cash/data")
                                    }
                                    variant="light"
                                    color="danger"
                                    size="sm"
                                    isDisabled={mutation.isLoading}
                                >
                                    Batal
                                </Button>
                                <Button
                                    color="primary"
                                    type="submit"
                                    isLoading={mutation.isLoading}
                                    size="sm"
                                    startContent={
                                        !mutation.isLoading && <LuSave />
                                    }
                                >
                                    Simpan
                                </Button>
                            </div>
                        </form>
                    )}
                </CardBody>
            </Card>
        </div>
    );
};

export default MasterCashCreate;
